import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { Country, Phone, User } from "@models";
import { map, take } from "rxjs/operators";
import { COUNTRY_ID_AUSTRIA, GERMANY_COUNTRY_ID, GERMANY_COUNTRY_ISO, UK_COUNTRY_ISO } from "@helpers/constants";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";
import { ProfileWithValidateable } from "@modules/main/components/profile/new-profile/entities/profile-model";
import { UserService } from "@main-services/user.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { BpSelectOption } from "@modules/core/components/bp-select/bp-select.component";
import { Observable } from "rxjs";
import { AgreementBundle, UserJourney } from "@modules/shared/terms-and-conditions/entities/terms-and-conditions.model";
import { TermsAndConditionsService } from "@modules/shared/terms-and-conditions/services/terms-and-conditions.service";
import {
	DYNAMIC_TERMS_TOKEN,
	TermsAndConditionsUpdatesComponent
} from "@modules/shared/terms-and-conditions/components/terms-and-conditions-updates.component";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { SegmentService } from "@main-services/segment.service";
import { BpInputComponent } from "@modules/core/components/bp-input/bp-input.component";
import { ErrorClass, ResponseError, ValidationError } from "bp-common-models-lib";
import { RudderstackService } from "@main-services/rudderstack.service";

@UntilDestroy()
@Component({
	selector: "profile",
	templateUrl: "./profile.component.html",
	styleUrls: ["./profile.component.scss"],
	standalone: false
})
export class ProfileComponent implements OnInit {
	@Output()
	public profileUpdated: EventEmitter<boolean> = new EventEmitter();

	@Input()
	public forceUpdate: EventEmitter<boolean> = new EventEmitter();

	@Input()
	public language: string;

	@ViewChild(DYNAMIC_TERMS_TOKEN) dynamic_terms: TermsAndConditionsUpdatesComponent;

	public profile$: Observable<ProfileWithValidateable> = this.profileFacade
		.getProfile$()
		.pipe(map((profile) => new ProfileWithValidateable(profile)));

	public countrySelected: Country = null;
	public phoneCodeOptions: Country[] = this.masterdataService.getAllCountries();
	public countryOptions: BpSelectOption[] = this.masterdataService.getAllCountriesBpSelectOption();
	public phoneCode: string = "";
	public showTCNotification: boolean = false;
	public showTCAdditionalInfo: boolean = true;
	public profileCountry: string = "";
	public profileCountryIso: string = "";
	public bundles: AgreementBundle[];
	public tempProfile: ProfileWithValidateable;
	public dynamicTermsAndConditionsActive: boolean = false;
	public isoAllowedCountries: string[] = [];
	public isGoogleSearchAddress: boolean = true;
	public isGoogleSearchBop: boolean = true;
	public pobCityNotFound: boolean = false;

	private wrongManualBoPFormat: boolean = false;
	private wrongBirthdateFormat: boolean = false;

	public constructor(
		private userService: UserService,
		private masterdataService: MasterdataService,
		private infoService: InfoService,
		private translationService: TranslationService,
		private profileFacade: NewProfileFacade,
		private termsAndConditionsService: TermsAndConditionsService,
		private readonly launchDarklyService: LaunchDarklyService,
		private segmentService: SegmentService,
		private rudderstackService: RudderstackService
	) {}

	public ngOnInit(): void {
		this.profileFacade.setTenancyTermsVerification(false);

		this.profile$.pipe(untilDestroyed(this)).subscribe((profile) => {
			this.profileCountry = profile.country_id;
			this.profileCountryIso = this.getCountry(profile.country_id).getIso();

			this.setInitialData(profile);
			this.listenForceUpdate(profile);
		});

		this.launchDarklyService
			.isActive$(LD_FLAG.TERMS_AND_CONDITIONS)
			.pipe(untilDestroyed(this))
			.subscribe((isActive) => {
				this.dynamicTermsAndConditionsActive = isActive;
			});

		this.isoAllowedCountries = [this.countrySelected.getIso()];
	}

	public user(): User {
		return this.userService.user;
	}

	public setCityNotFound(): void {
		this.pobCityNotFound = true;
	}

	private getCountry(countryId: string): Country {
		return this.masterdataService.countries.find((country: Country) => country.getId() === countryId);
	}

	public setInitialData(profile: ProfileWithValidateable): void {
		if (profile.country_id) {
			this.countrySelected = this.getCountry(profile.country_id);

			if (!profile.mobile) {
				this.phoneCode = "" + this.getCountry(this.countrySelected?.getId()).getPhoneCode();
				profile.phonenumber = "";
			} else {
				const countryPhone = this.masterdataService.countries.find((country) =>
					profile.mobile.startsWith("+" + country.getPhoneCode())
				);
				// getting phone code based on the already inserted mobile number
				this.phoneCode = "" + countryPhone.getPhoneCode();
				profile.phonenumber = "";
				profile.mobile = "";
			}
		} else {
			// In case data is reset in the backend, this method sets Austria as the base country for the user
			this.countrySelected = this.getCountry(COUNTRY_ID_AUSTRIA);
			profile.country_id = COUNTRY_ID_AUSTRIA;
			profile.countrySelected = this.countrySelected;
			profile.phone_number_verified = false;
		}
	}

	public update(profile: ProfileWithValidateable): void {
		if (!profile.accepted_terms_data) {
			profile.wiggle("accepted_terms_data");
			this.infoService.setError(this.translationService.translate("verification_confirm_saving_level3_data"));
			return;
		}

		//edge case in case user is austrian and creates an account without data (this can be done via mobile app)
		if (!profile.country_id) {
			profile.country_id = COUNTRY_ID_AUSTRIA;
		}

		if (!this.checkAddressStreetFormat(profile.street)) {
			profile.setError("street", this.translationService.translate("manual_address_empty_street"));
			return;
		}

		if (this.wrongManualBoPFormat) {
			profile.setError("manual_date_of_birth", this.translationService.translate("manual_pob_error"));
			return;
		}

		if (this.wrongBirthdateFormat) {
			profile.setError("fullBirthday", this.translationService.translate("birthday"));
			return;
		}

		this.profileFacade
			.update(profile)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.infoService.setSuccess(this.translationService.translate("info_text_successfully_saved"));
					this.profileUpdated.emit(true);
				},
				error: (error) => {
					if (error instanceof ErrorClass) {
						if (error instanceof ValidationError) {
							profile.accepted_terms_data = false;
							profile.setValidationErrors(error);
							this.infoService.setError(error, { ignore: ["user.lvl2.already.used"] }, true);

							// Extend the API answer with some rules in order to display the errors inside the UI
							if (error.responseErrors.some((re) => re.meta && re.meta.field === "birthday")) {
								profile.cloneError("birthday", "fullBirthday");
							}
							const mobileAlreadyUsed: ResponseError = error.responseErrors.find(
								(re) => re.code === "user.lvl2.already.used"
							);
							if (mobileAlreadyUsed) {
								profile.setError("mobile", mobileAlreadyUsed.title);
							}
							const tenancyTermsError: ResponseError = error.responseErrors.find(
								({ status, code }: ResponseError) =>
									code === "tenancy.terms.update" && Number(status) === 422
							);
							if (tenancyTermsError) {
								const { meta } = tenancyTermsError;
								this.bundles = JSON.parse(meta.value);

								this.profileFacade.setTenancyTermsVerification(true);
								this.tempProfile = new ProfileWithValidateable(profile);
							}
						} else {
							this.infoService.setError(error);
						}
					} else {
						console.error(error);
					}
				}
			});
	}

	public listenForceUpdate(profile: ProfileWithValidateable): void {
		this.forceUpdate.pipe(untilDestroyed(this)).subscribe((val) => {
			if (val) {
				// Auto-accept profile slider:
				profile.accepted_terms_data = true;
				// Validate data with API and emit profile update:
				this.update(profile);
			}
		});
	}

	public selectedCountryName(profile: ProfileWithValidateable): string {
		const country =
			this.masterdataService.getAllCountries().find((c) => c._id === profile.country_id) || new Country();
		return country.getName();
	}

	public onCountrySelect(country_id: string, profile: ProfileWithValidateable): void {
		profile.country_id = country_id;
		this.countrySelected = this.getCountry(country_id);
		this.isoAllowedCountries = [this.countrySelected.getIso()];

		profile.country_id = country_id;
		profile.iso = this.countrySelected.getIso();
		this.isGoogleSearchAddress = true;

		this.resetAddressFormFields(profile);

		if (this.profileCountry !== country_id) {
			this.showTCNotification = true;

			this.segmentService.track({
				name: "T&C Bottom Sheet Viewed",
				properties: {
					screen_it_happened_on: "Address Page",
					flow_it_happened_on: "Personal Details"
				}
			});
			this.rudderstackService.track({
				name: "T&C Bottom Sheet Viewed",
				properties: {
					screen_it_happened_on: "Address Page",
					flow_it_happened_on: "Personal Details"
				}
			});
		}

		if (this.profileCountryIso === UK_COUNTRY_ISO) {
			this.showTCAdditionalInfo = [GERMANY_COUNTRY_ISO].includes(this.countrySelected.getIso());
		}

		if (this.profileCountryIso !== UK_COUNTRY_ISO) {
			this.showTCAdditionalInfo = [GERMANY_COUNTRY_ISO, UK_COUNTRY_ISO].includes(this.countrySelected.getIso());
		}
	}

	public onMobileChange(phone: Phone, profile: ProfileWithValidateable): void {
		profile.phonecode = phone.code;
		profile.phonenumber = phone.number;
	}

	public getAddressData(event, profile: ProfileWithValidateable): void {
		this.isGoogleSearchAddress = !this.isGoogleSearchAddress;

		profile.street = event.street;
		profile.zipcode = event.postCode;
		profile.city = event.city;
	}

	public trackPlaceOfBirthClick(): void {
		this.segmentService.track({
			name: "Pob_Field_Clicked",
			properties: {
				screen_it_happened_on: "Place of Birth Page",
				flow_it_happened_on: "Personal Details"
			}
		});
		this.rudderstackService.track({
			name: "Pob_Field_Clicked",
			properties: {
				screen_it_happened_on: "Place of Birth Page",
				flow_it_happened_on: "Personal Details"
			}
		});
	}

	public getPlaceOfBirth(event, profile: ProfileWithValidateable): void {
		profile.place_of_birth = event?.place_of_birth;
	}

	public onPlaceOfBirthValueChange(event: string, profile: ProfileWithValidateable): void {
		profile.place_of_birth = event;
	}

	public validateDateOfBirth(event: FocusEvent, profile: ProfileWithValidateable): void {
		const input = event.target as HTMLInputElement;
		const birthdate_input = input.value;
		// Trim whitespace from the entire input
		const trimmed = birthdate_input.trim();

		const regex = /^(0?[1-9]|[12][0-9]|3[01])[.](0?[1-9]|1[012])[.]\d{4}$/;

		if (regex.test(trimmed)) {
			this.wrongBirthdateFormat = false;
		} else {
			profile.setError("fullBirthday", this.translationService.translate("birthday"));
			this.wrongBirthdateFormat = true;
		}
	}

	public validateManualPlaceOfBirth(event: FocusEvent, profile: ProfileWithValidateable): void {
		const input = event.target as HTMLInputElement;
		const place_of_birth_input = input.value;
		// Trim whitespace from the entire input
		const trimmed = place_of_birth_input.trim();

		// Check if there's exactly one comma
		const parts = trimmed.split(",");
		if (parts.length !== 2) {
			profile.setError("manual_date_of_birth", this.translationService.translate("manual_pob_error"));
			this.wrongManualBoPFormat = true;
		}

		// Extract city and country, trimming whitespace
		const city = parts[0].trim();
		const country = parts[1].trim();

		// Check that both city and country are non-empty
		if (city.length === 0 || country.length === 0) {
			profile.setError("manual_date_of_birth", this.translationService.translate("manual_pob_error"));
			this.wrongManualBoPFormat = true;
		}
		this.wrongManualBoPFormat = false;
	}

	public switchBetweenAddressesForms(profile: ProfileWithValidateable): void {
		this.isGoogleSearchAddress = !this.isGoogleSearchAddress;
		this.resetAddressFormFields(profile);
	}

	public switchBetweenManualBop(profile: ProfileWithValidateable): void {
		this.isGoogleSearchBop = !this.isGoogleSearchBop;
		this.resetPobFields(profile);
	}

	public onInputFocusOut(event: FocusEvent, profile: ProfileWithValidateable): void {
		const input = event.target as HTMLInputElement;

		if (!this.checkAddressStreetFormat(input.value)) {
			profile.setError("street", this.translationService.translate("manual_address_empty_street"));
		}
	}
	public onValueChange(event: string, streetInput: BpInputComponent): void {
		if (event.length > 0 && !this.checkAddressStreetFormat(event)) {
			streetInput.inputGroup.nativeElement.focus();
			streetInput.error = this.translationService.translate("manual_address_empty_street");
		} else {
			streetInput.error = "";
		}
	}

	public checkAddressStreetFormat(value: string): boolean {
		// We don't need local validation for UK addresses
		return true;
	}

	public get dynamicTermsInvalid(): boolean {
		return this.dynamicTermsAndConditionsActive && !!this.dynamic_terms?.termsAndConditionsForm?.invalid;
	}

	public onTenancyTermsAccept(): void {
		const acceptedBundleVersionIds = this.bundles.map(
			(agreement: AgreementBundle) => agreement.agreement_bundle_version_id
		);

		if (this.bundles.length > 0 && this.dynamicTermsInvalid) {
			this.dynamic_terms.wiggleIt();
			return;
		}

		this.tempProfile.accepted_terms_data = true;
		this.tempProfile.agreement_bundles = acceptedBundleVersionIds;

		this.termsAndConditionsService.saveTerms([UserJourney.Onboarding], acceptedBundleVersionIds);
		this.update(this.tempProfile);

		this.segmentService.track({
			name: "Bundle Link Tapped",
			properties: {
				screen_it_happened_on: "Address Page",
				flow_it_happened_on: "Personal Details",
				bundle_id: acceptedBundleVersionIds
			}
		});
		this.rudderstackService.track({
			name: "Bundle Link Tapped",
			properties: {
				screen_it_happened_on: "Address Page",
				flow_it_happened_on: "Personal Details",
				bundle_id: acceptedBundleVersionIds
			}
		});
	}

	public trackTermsData(): void {
		this.segmentService.track({
			name: "Agree T&C Button Tapped",
			properties: {
				screen_it_happened_on: "Address Page",
				flow_it_happened_on: "Personal Details"
			}
		});
		this.rudderstackService.track({
			name: "Agree T&C Button Tapped",
			properties: {
				screen_it_happened_on: "Address Page",
				flow_it_happened_on: "Personal Details"
			}
		});
	}

	public displayTCAdditionalInfo(): boolean {
		return this.profileCountryIso !== this.countrySelected.getIso() && !this.tempProfile?.agreement_bundles;
	}

	public resetAddressFormFields(profile: ProfileWithValidateable): void {
		profile.street = "";
		profile.zipcode = "";
		profile.city = "";
	}

	public resetPobFields(profile: ProfileWithValidateable): void {
		profile.place_of_birth = "";
	}

	protected readonly GERMANY_COUNTRY_ID = GERMANY_COUNTRY_ID;
}
